import React, { SyntheticEvent, ReactText, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MultiSelectInput } from "fe-shared/src/components";
import { FilterIcon } from "fe-shared/src/components/Icons";
import { DataGridFilters } from "fe-shared/src/components/DataGrid/modules/types";

import UserContext from "contexts/userContext";

import { ASSIGNEE_COL_ID } from "../RequestsDataGrid/modules/config";

import styles from "./RequestsAdditionalFilters.module.scss";

export enum FilterName {
  ShowAll = "ShowAll",
  Mine = "Mine",
  Flagged = "Flagged",
}

interface Props {
  filters: DataGridFilters;
  updateFilters: (filters: DataGridFilters) => void;
}

const RequestsAdditionalFilters = (props: Props) => {
  const { filters, updateFilters } = props;
  const { t } = useTranslation();
  const user = useContext(UserContext);

  const userName = `${user?.firstName} ${user?.lastName}`;
  const isMineChecked = filters?.[ASSIGNEE_COL_ID]?.value === userName;
  // The flag filter rides the warning column: it is the only real column it can hang on, and a
  // key with no column is dropped the moment the grid rebuilds its own filter model.
  const isFlaggedChecked = filters?.warning?.value === "true";
  const checkedCount = [isMineChecked, isFlaggedChecked].filter(Boolean).length;

  const value = useMemo(() => {
    const selected: FilterName[] = [];

    if (!checkedCount) selected.push(FilterName.ShowAll);
    if (isMineChecked) selected.push(FilterName.Mine);
    if (isFlaggedChecked) selected.push(FilterName.Flagged);

    return selected;
  }, [checkedCount, isMineChecked, isFlaggedChecked]);

  const options = useMemo(
    () =>
      Object.values(FilterName).map((name) => ({
        value: name,
        label: t(`pickupRequests.grid.additionalFilters.${name}`),
      })),
    [t]
  );

  const placeholder = (
    <>
      <FilterIcon fillColor={null} />
      {t("pickupRequests.grid.additionalFilters.label")}
      {checkedCount ? ` +${checkedCount}` : ""}
    </>
  );

  const onChange = (
    _event: SyntheticEvent,
    _values: ReactText[],
    selectedValue: ReactText
  ) => {
    switch (selectedValue) {
      case FilterName.Mine:
        updateFilters({
          ...filters,
          [ASSIGNEE_COL_ID]: {
            type: "textFilter",
            value: isMineChecked ? "" : userName,
          },
        });
        break;
      case FilterName.Flagged:
        updateFilters({
          ...filters,
          warning: {
            type: "textFilter",
            value: isFlaggedChecked ? "" : "true",
          },
        });
        break;
      case FilterName.ShowAll:
        // Already the state when nothing else is checked, so only a clear is meaningful here.
        if (checkedCount) {
          updateFilters({
            ...filters,
            [ASSIGNEE_COL_ID]: { type: "textFilter", value: "" },
            warning: { type: "textFilter", value: "" },
          });
        }
        break;
      default:
    }
  };

  return (
    <MultiSelectInput
      id="requests-additional-filters"
      value={value}
      options={options}
      placeholder={placeholder}
      className={styles.select}
      onChange={onChange}
    />
  );
};

export default RequestsAdditionalFilters;
